/**
 * A project at a glance: how ready it is (from "O que falta"), whether it has an MVP, the next stage,
 * and its list of pending items. On Início, one per project; on Projeto › Visão, the active one.
 */

import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ClipboardList, Loader2 } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { ensurePendingList } from '../lib/pendingList'
import { latestReadiness } from '../lib/home'
import type { LaunchStage } from '../lib/repositoryRun'
import type { RepositoryLink, WorkOrder } from '../lib/types'
import { Badge } from './primitives'

export function ProjectCard({ repository, orders, isActive, onUse }: { repository: RepositoryLink; orders: WorkOrder[]; isActive: boolean; onUse: () => void }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const readiness = latestReadiness(orders, repository.id)
  const pending = useMutation({
    mutationFn: () => ensurePendingList(repository.name),
    onSuccess: (path) => {
      void queryClient.invalidateQueries({ queryKey: ['docs'] })
      navigate(`/project/docs?path=${encodeURIComponent(path)}`)
    },
  })

  return (
    <div className={`home__project${isActive ? ' is-active' : ''}`}>
      <div className="row row--between">
        <span className="bold truncate">{repository.name}</span>
        {isActive ? (
          <Badge tone="accent">{t('palette.projectActive')}</Badge>
        ) : (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onUse}>{t('home.use')}</button>
        )}
      </div>

      {readiness?.ready != null ? (
        <div className="stack" style={{ gap: 6 }}>
          <div className="row row--tight">
            <span className="home__ready">{readiness.ready}%</span>
            <div className="readiness__bar" style={{ flex: 1 }} role="progressbar" aria-valuenow={readiness.ready} aria-valuemin={0} aria-valuemax={100} aria-label={t('home.ready')}>
              <span style={{ width: `${readiness.ready}%` }} />
            </div>
          </div>
          <div className="row row--tight row--wrap tiny">
            {readiness.mvp !== null && <span className={`readiness__chip${readiness.mvp ? ' is-ok' : ''}`}>{readiness.mvp ? t('project.mvpYes') : t('project.mvpNo')}</span>}
            {readiness.stage && <span className="readiness__chip is-next">{t('project.next')}: {t(`project.stage.${readiness.stage as LaunchStage}`)}</span>}
          </div>
        </div>
      ) : (
        <span className="tiny muted">{t('home.noReadiness')}</span>
      )}

      <div className="row row--tight">
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => pending.mutate()} disabled={pending.isPending}>
          {pending.isPending ? <Loader2 size={13} className="spin" /> : <ClipboardList size={13} />} {t('project.pending')}
        </button>
        {pending.isError && <span className="tiny run-bar__notice--bad">{t('project.pendingFailed')}</span>}
      </div>
    </div>
  )
}
